import React, { ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { GameMoveModel } from '../types/GamesModel';
import { Chessviewer } from '../utils/Chessviewer';
import GameContext from '../context/GameContext';
import { Key } from 'chessground/types';
import { DrawShape } from 'chessground/draw';
import { getIcon } from '../utils/ChessTypes';
import GamesListItemControls from './GamesListItemControls';
import { useAppSelector } from '../store';
import GamesListItemEvaluationBar from './GamesListItemEvaluationBar';
import { Color } from '../types/Color';
import { MoveType } from '../types/MoveType';
import AnnotationsContext from '../context/AnnotationsContext';
import ReactDOMServer from 'react-dom/server';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsUpDownLeftRight } from '@fortawesome/free-solid-svg-icons';
import { stockfishEngine, EngineEval } from '../utils/StockfishEngine';
import { whiteWinPct } from './winChance';
import { usePractice } from './PracticeProvider';
import { useCorrections } from './CorrectionsProvider';
import { gradeMove } from './correctionGrading';
import PracticeBelowBoard from './PracticeBelowBoard';

// Grade a dragged move by the win chance the mover gave up, on the app's move classes. A live cue,
// not the server's evaluation.
function classifyDrop(dropPct: number): MoveType {
  if (dropPct >= 20) return MoveType.Blunder;
  if (dropPct >= 10) return MoveType.Mistake;
  if (dropPct >= 5) return MoveType.Inaccuracy;
  if (dropPct >= 2) return MoveType.Good;
  return MoveType.Best;
}

// The refutation arrow brush for an error move, by severity (registered in Chessviewer). Null for
// non-errors.
function severityBrush(moveType: MoveType | null): string | null {
  switch (moveType) {
    case MoveType.Blunder:
      return 'scBlunder';
    case MoveType.Mistake:
      return 'scMistake';
    case MoveType.Miss:
      return 'scMiss';
    case MoveType.Inaccuracy:
      return 'scInaccuracy';
    default:
      return null;
  }
}

// The arrow after a practice move, by grade; green for a good move.
function practiceArrowBrush(moveType: MoveType | null): string {
  return severityBrush(moveType) ?? 'green';
}

// Just the board frame (eval bar, chessground, step controls). GameBodyWithPanel lays out the moves
// table and comment panel beside it.
export default function GamesListItemChessboard(): ReactNode {
  const { game, orientation, currentMove } = useContext(GameContext);
  const { color, pgn: path } = useAppSelector((state) => state.paramsReducer.params);
  const { chessviewerRef, setCurrentMove, setAnalysisWhiteWin, setDragging } =
    useContext(GameContext);
  const practice = usePractice();
  const { recordOutcome } = useCorrections();
  // Live practice state for the once-registered viewer callbacks.
  const practiceActiveRef = useRef(false);
  practiceActiveRef.current = practice.active;
  const selectedRef = useRef(practice.selected);
  selectedRef.current = practice.selected;
  const recordOutcomeRef = useRef(recordOutcome);
  recordOutcomeRef.current = recordOutcome;
  const hasOutcome = practice.outcome != null;
  // Once an answer is committed, exploring is no longer a solve, so the overlays practice hides (best
  // move arrow, badges) come back.
  const answeredRef = useRef(false);
  answeredRef.current = hasOutcome;
  // Explore mode starts with a dragged move, so the banner follows the viewer's exploring state.
  const [exploring, setExploring] = useState(false);
  const { openPanel } = useContext(AnnotationsContext);
  // Per-FEN engine results, so stepping through an exploration line doesn't re-run the engine.
  const evalCacheRef = useRef<Map<string, EngineEval>>(new Map());
  // Bumped per analysis so a slow earlier evaluation can't overwrite a newer one.
  const analysisAttemptRef = useRef(0);
  const movePgnForModal = useAppSelector((state) => state.uiReducer.movePgnForModal);
  const moveRefForModal = useAppSelector((state) => state.uiReducer.moveRefForModal);
  const elementRef = useRef<HTMLDivElement>(null);
  // The side at creation; flips are applied to the live board below without a rebuild.
  const orientationRef = useRef(orientation);

  const movesModel = useMemo(() => {
    return game.Moves.flatMap((m) => [m.White, m.Black]).filter(Boolean) as GameMoveModel[];
  }, [game.Moves]);

  useEffect(() => {
    if (elementRef.current == null) {
      return;
    }
    const viewer = new Chessviewer(elementRef.current, orientationRef.current);
    chessviewerRef.current = viewer;

    // Both fields, not just mateIn: mate 0 carries the winner in the score's sign (see EngineEval).
    const winOf = (e: EngineEval) => whiteWinPct(e.scoreCp, e.mateIn);
    const evalFen = (fen: string): Promise<EngineEval> => {
      const cached = evalCacheRef.current.get(fen);
      if (cached) {
        return Promise.resolve(cached);
      }
      return stockfishEngine.evaluate(fen).then((res) => {
        evalCacheRef.current.set(fen, res);
        return res;
      });
    };

    // While exploring, grade the landed position with the local engine: eval bar, the engine's move as
    // an arrow, and a move-class badge on the last move.
    viewer.onExplore(() => {
      const last = viewer.getCurrentMove();
      const afterFen = viewer.chess.fen();
      const attempt = ++analysisAttemptRef.current;
      viewer.cg.setAutoShapes([]);
      viewer.cg.setShapes([]);
      evalFen(afterFen)
        .then((afterEval) => {
          if (analysisAttemptRef.current !== attempt) {
            return;
          }
          const afterWhite = winOf(afterEval);
          setAnalysisWhiteWin(afterWhite);
          // While solving, no best-move arrow; once answered, exploring reveals it.
          if (
            (!practiceActiveRef.current || answeredRef.current) &&
            afterEval.bestMove &&
            afterEval.bestMove.length >= 4
          ) {
            viewer.cg.setShapes([
              {
                orig: afterEval.bestMove.slice(0, 2) as Key,
                dest: afterEval.bestMove.slice(2, 4) as Key,
                brush: 'green',
              },
            ]);
          }
          if (last == null) {
            return;
          }
          evalFen(last.before).then((beforeEval) => {
            if (analysisAttemptRef.current !== attempt) {
              return;
            }
            const beforeWhite = winOf(beforeEval);
            const moverIsWhite = last.color === 'w';
            const moverBefore = moverIsWhite ? beforeWhite : 100 - beforeWhite;
            const moverAfter = moverIsWhite ? afterWhite : 100 - afterWhite;
            const Icon = getIcon(classifyDrop(moverBefore - moverAfter));
            const markup = ReactDOMServer.renderToStaticMarkup(
              <svg width="20" height="20" viewBox="0 0 8 8">
                <Icon />
              </svg>,
            );
            viewer.cg.setAutoShapes([{ orig: last.to as Key, customSvg: { html: markup } }]);
          });
        })
        .catch(() => {});
    });

    // Keep the toggle in sync, including when the viewer turns dragging off itself. Leaving exploration
    // drops the live eval too.
    viewer.onDraggableChange((enabled) => {
      setDragging(enabled);
      if (!enabled) {
        setAnalysisWhiteWin(null);
      }
    });
    // The "Explore mode" banner follows exploration.
    viewer.onExploringChange(setExploring);

    // Practice: grade the dropped answer against the solve position with the browser engine, record the
    // outcome, move the eval bar, and arrow the best continuation coloured by the grade.
    viewer.onPracticeMove((orig, dest) => {
      const sel = selectedRef.current;
      if (sel == null) return;
      gradeMove(sel.Fen, orig, dest, sel.SideToMove, sel.Solution, sel.PlayedMove, sel.MoveType)
        .then((res) => {
          if (res == null) return;
          // Include the position after the move and its best line for the reveal's "From here:" row.
          recordOutcomeRef.current(sel, {
            ...res.outcome,
            afterFen: res.afterFen,
            bestLineUci: res.bestLineUci,
          });
          setAnalysisWhiteWin(res.afterWhiteWin);
          // Badge the move with its classification icon on the square it landed on.
          const autoShapes: DrawShape[] = [];
          if (res.outcome.moveType != null) {
            const Icon = getIcon(res.outcome.moveType);
            const markup = ReactDOMServer.renderToStaticMarkup(
              <svg width="20" height="20" viewBox="0 0 8 8">
                <Icon />
              </svg>,
            );
            autoShapes.push({ orig: dest as Key, customSvg: { html: markup } });
          }
          viewer.cg.setAutoShapes(autoShapes);
          viewer.cg.setShapes(
            res.firstLineArrow
              ? [
                  {
                    orig: res.firstLineArrow[0],
                    dest: res.firstLineArrow[1],
                    brush: practiceArrowBrush(res.outcome.moveType),
                  },
                ]
              : [],
          );
        })
        .catch(() => {});
    });

    // Tap the board to enter explore mode with dragging armed. A scroll swipe fires no click, so this
    // doesn't fight scrolling on mobile.
    const boardEl = elementRef.current;
    const onBoardTap = () => {
      if (!viewer.isDraggable()) {
        viewer.setDraggable(true);
        setDragging(true);
        stockfishEngine.preload();
      }
    };
    boardEl.addEventListener('click', onBoardTap);

    // Create once; flips are handled by the orientation effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => boardEl.removeEventListener('click', onBoardTap);
  }, [chessviewerRef]);

  // Follow orientation changes (flip button) on the live board without a rebuild.
  useEffect(() => {
    chessviewerRef.current?.cg.set({ orientation });
  }, [chessviewerRef, orientation]);

  // Landing on a stored game move drops live analysis, so the eval bar shows the stored evaluation.
  useEffect(() => {
    setAnalysisWhiteWin(null);
  }, [currentMove, setAnalysisWhiteWin]);

  useEffect(() => {
    const chessviewer = chessviewerRef.current;
    if (chessviewer == null) {
      return;
    }

    chessviewer.onPlayback((move) => {
      // Clear shapes left over from exploration.
      chessviewer.cg.setAutoShapes([]);
      chessviewer.cg.setShapes([]);
      if (move == null) {
        return;
      }
      const pgn = chessviewer.chess.pgn();
      chessviewer.chess.undo();
      const prev = chessviewer.chess.pgn();
      chessviewer.chess.move(move);

      let moveModel = movesModel.find((m) => m.Pgn == pgn);
      let prevMoveModel = movesModel.find((m) => m.Pgn == prev);

      if (!moveModel) {
        const curr = chessviewer.getCurrentMove();
        const prevMove = chessviewer.getPreviousMove();
        // Match by FEN, falling back to placement + side to move + castling: the stored FEN always records
        // the en-passant target after a double push, while chess.js omits it when no capture is legal.
        const matchFen = (target?: string | null) => {
          if (target == null) return undefined;
          const exact = movesModel.find((m) => m.Fen === target);
          if (exact) return exact;
          const key = (f: string) => f.split(' ').slice(0, 3).join(' ');
          const targetKey = key(target);
          return movesModel.find((m) => key(m.Fen) === targetKey);
        };
        moveModel = matchFen(curr?.after);
        prevMoveModel = matchFen(prevMove?.after);
      }
      if (moveModel) {
        setCurrentMove(moveModel);
        const autoShapes: DrawShape[] = [];
        if (moveModel.MoveType != null) {
          const MyIcon = getIcon(moveModel.MoveType);
          const markup = ReactDOMServer.renderToStaticMarkup(
            <svg width="20" height="20" viewBox="0 0 8 8">
              <MyIcon />
            </svg>,
          );
          autoShapes.push({ orig: move.to as Key, customSvg: { html: markup } });
        }
        // The opponent's refutation (PatternFutureMove), from the position after the move, coloured by
        // severity. Only error moves carry one.
        const futMove = moveModel.PatternFutureMove;
        const brush = severityBrush(moveModel.MoveType);
        if (futMove != null && futMove.length >= 4 && brush != null) {
          autoShapes.push({
            orig: futMove.slice(0, 2) as Key,
            dest: futMove.slice(2, 4) as Key,
            brush,
          });
        }
        chessviewer.cg.setAutoShapes(autoShapes);
      }
      const bestMove = prevMoveModel?.BestMove;
      // No best-move arrow while correcting.
      if (chessviewer.direction == 'forward' && bestMove != null && !practiceActiveRef.current) {
        const orig = bestMove.slice(0, 2);
        const dest = bestMove.slice(2, 4);
        chessviewer.cg.setShapes([{ orig: orig as Key, dest: dest as Key, brush: 'green' }]);
      }
    });
    chessviewer.loadPgn(game.Pgn);
    // A deep link (?move=12w) jumps to that move and opens its comment panel.
    const refMatch = moveRefForModal != null ? /^(\d+)([wb])$/.exec(moveRefForModal) : null;
    const refTarget = refMatch
      ? movesModel.find(
          (m) =>
            m.Number === parseInt(refMatch[1], 10) &&
            m.Color === (refMatch[2] === 'w' ? Color.White : Color.Black),
        )
      : undefined;
    if (refTarget != null) {
      chessviewer.go(refTarget.Pgn);
      // On mobile also raise the comment sheet.
      openPanel();
    } else if (movePgnForModal != null && movePgnForModal != '') {
      // Opened by clicking a commented move.
      chessviewer.go(movePgnForModal);
    } else if (path != null && path != '') {
      chessviewer.go(path);
    } else {
      const firstMove = game.Moves.at(0)?.White;
      if (firstMove) {
        chessviewer.go(firstMove.Pgn);
      }
    }
  }, [
    chessviewerRef,
    color,
    game.Moves,
    game.Pgn,
    movesModel,
    path,
    movePgnForModal,
    moveRefForModal,
    openPanel,
    setCurrentMove,
  ]);

  // Arm the board for the selected mistake at the position before it. Re-runs on Next or Try again;
  // with an outcome in, the board stays on the played move.
  useEffect(() => {
    const viewer = chessviewerRef.current;
    if (viewer == null) return;
    if (practice.active && practice.selected != null && !hasOutcome) {
      viewer.cg.setShapes([]);
      viewer.cg.setAutoShapes([]);
      viewer.practiceFrom(practice.prevMovePgn);
      setAnalysisWhiteWin(
        practice.selected.Evaluation != null ? whiteWinPct(practice.selected.Evaluation) : null,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practice.active, practice.selected?.MoveId, hasOutcome, practice.prevMovePgn]);

  // Leaving practice restores the game and lands on the mistake move, unless the board already moved.
  const prevPracticeActiveRef = useRef(false);
  useEffect(() => {
    const viewer = chessviewerRef.current;
    if (viewer == null) {
      prevPracticeActiveRef.current = practice.active;
      return;
    }
    if (prevPracticeActiveRef.current && !practice.active) {
      const wasOnBoard = viewer.isPracticing() || viewer.isExploring();
      viewer.stopPractice();
      if (wasOnBoard) {
        const target = practice.selectedMove?.Pgn;
        if (target) viewer.go(target);
        else viewer.go('first');
      }
    }
    prevPracticeActiveRef.current = practice.active;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practice.active, practice.selectedMove]);

  // Leave explore mode from the banner, back to the game move.
  const exitExplore = () => {
    const viewer = chessviewerRef.current;
    if (viewer == null) {
      return;
    }
    viewer.setDraggable(false);
    setDragging(false);
  };

  return (
    <div className="cm-board-frame d-flex justify-content-center">
      <div className="mb-2">
        {exploring && !practice.active ? (
          <div className="cm-explore-badge" role="status">
            <FontAwesomeIcon icon={faArrowsUpDownLeftRight} />
            <span>Explore mode. Drag a move or step with the arrows.</span>
            <button type="button" className="cm-explore-exit" onClick={exitExplore}>
              Exit
            </button>
          </div>
        ) : null}
        <GamesListItemEvaluationBar />
        <div className="cm-chessboard mb-1" ref={elementRef}></div>
        {/* While correcting, the practice prompt / reveal replaces the step controls. */}
        {practice.active ? <PracticeBelowBoard /> : <GamesListItemControls />}
      </div>
    </div>
  );
}
